import React, { Component } from "react";
import Paper from "../../assets/climbing-light-paper.pdf";
import BackButton from "../../component/BackButton";
import ClimbingNet_1 from "../../assets/images/climbing-net-design.svg";
import ClimbingNet_2 from "../../assets/images/small-prototype.svg";
import ClimbingNet_3 from "../../assets/images/final-prototype.svg";
import ClimbingNet_4 from "../../assets/images/climbingnet-testing.svg";

class ProjectDetailClimbingLight extends Component {
  render() {
    return (
      <React.Fragment>
        <div className="container mt-5">
          <BackButton />
          <div className="sm-space"></div>
          <div className="project-title text-left">Climbing Lights</div>
          <div className="sm-space"></div>
          <div className="row center-items">
            <div className="project-info col-lg-3 col-sm-12">
              <div>
                <div className="project-subheader">Project Summary</div>
                <div className="project-subheader-detail">
                  <b>Client: </b>
                  Physical Interaction Design
                  <div className="sm-space"></div>
                  <b>Role: </b>
                  User Researcher
                  <div className="sm-space"></div>
                  <b>Technology: </b>
                  RGB LEDs, Flex Sensor, Arduino Uno
                  <div className="sm-space"></div>
                  <b>Documentation: </b>
                  <a href={Paper} target="_blank" rel="noopener noreferrer">
                    Download Paper
                  </a>
                  <div className="sm-space"></div>
                  <b>Video</b>
                  <div>
                    <div className="sm-space"></div>
                    <iframe
                      title="video-climbing-light"
                      className="video-climbing-net"
                      src="https://player.vimeo.com/video/368869269"
                      frameborder="0"
                      allow="autoplay; fullscreen"
                      allowfullscreen
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
            <div className="project-description col-lg-9 col-sm-12">
              <p>
                Climbing with Light is an interactive climbing installation that
                integrates light and sound to enhance the play experience in
                outdoor environments. Designed specifically for Swedish winter
                playgrounds, the installation aims to create an engaging and
                stimulating atmosphere during the darker months, encouraging
                children to remain active and playful despite limited daylight.
                <div className="space"></div>
                <div className="project-subheader">
                  Sweden, located in the northern hemisphere, is characterized
                  by its long and dark winter season, which typically lasts for
                  over five months — from November or December through March or
                  February each year. This extended period of limited daylight
                  significantly influences outdoor activities, particularly for
                  children, and serves as a key contextual factor in designing
                  engaging winter play environments.
                </div>
                <p className="detail-content">
                  During the winter months, children have limited opportunities
                  for outdoor play after school due to the prolonged darkness
                  and cold weather. As a result, many tend to stay indoors,
                  often engaging with digital devices instead of participating
                  in physical activities. This reduced outdoor engagement can
                  negatively impact both their physical health and social
                  development. This research aims to explore the opportunities
                  and challenges involved in designing a playground that remains
                  engaging and functional under dark-winter conditions. The
                  focus is on fostering playfulness and promoting social
                  interaction among children through thoughtful design
                  interventions. The climbing nets serve as the central design
                  element and object of study, representing a versatile and
                  physically engaging structure suitable for adaptation to
                  winter environments.
                </p>
                <div className="space"></div>
                <div className="project-subheader-summary">Design Concepts</div>
                <p className="detail-content">
                  We developed three models of interaction to explore how light
                  and sound can enhance the play experience within the climbing
                  nets. The first model, Idle Mode, is activated when no
                  interaction occurs. Its purpose is to attract attention by
                  displaying subtle lighting patterns and ambient sounds,
                  signaling to potential players that the structure offers an
                  unusual and engaging play experience. The second model,
                  Single-Player Mode, is triggered when a child interacts with
                  the climbing net. In this state, LED lights illuminate and
                  location-based sounds are played in response to the user’s
                  movement. Both the light and sound effects stop once the
                  interaction ends, creating a clear feedback loop between the
                  player’s actions and the system’s responses. The final model,
                  Multi-Player Mode, activates when multiple users interact
                  simultaneously. In this mode, intersecting inputs generate a
                  layered musical composition, encouraging collaboration and
                  shared play experiences while emphasizing the social dimension
                  of the design.
                </p>
                <div className="project-image">
                  <img
                    className="cover"
                    src={ClimbingNet_1}
                    alt="climbing-net-design"
                  />
                  <p>Design Vision</p>
                </div>
                <div className="space"></div>
                <div className="project-subheader-summary">
                  Design Challenges
                </div>
                <div className="detail-content">
                  During the design process, we identified two primary
                  challenges in developing the interactive climbing nets. The
                  first challenge involved preventing auditory cacophony in the
                  multi-player mode, ensuring that multiple simultaneous
                  interactions would generate a harmonious and pleasant sound
                  composition rather than noise. Achieving this required careful
                  tuning of the sound system and designing responsive rules that
                  balanced individual and collective inputs. The second
                  challenge was selecting materials suitable for extreme winter
                  conditions, capable of functioning reliably in temperatures
                  below –20°C. This necessitated the exploration of durable,
                  weather-resistant components that could withstand both cold
                  exposure and physical impact, ensuring safety and consistent
                  performance throughout the season.
                </div>
                <div className="space"></div>
                <div className="project-subheader-summary">
                  Development Phase
                </div>
                <div>
                  <div className="detail-header">1. Small Prototype</div>
                  <div className="detail-content">
                    At the initial stage of development, we constructed a scaled
                    prototype to visualize the intended design and evaluate the
                    feasibility of the concept. The prototype took the form of a
                    half-spherical structure, designed to represent the core
                    geometry of the final climbing net installation. The
                    structure was built from wood, featuring a 26 cm diameter
                    base and a 9 cm diameter top, providing a stable yet compact
                    form for testing. The climbing net itself consisted of four
                    rows and eight columns, woven using fishing string to
                    simulate the flexibility and tension of the real material.
                    This early prototype allowed the design team to assess
                    spatial proportions, evaluate interaction possibilities, and
                    validate the structural concept before moving toward
                    larger-scale implementation.
                  </div>
                  <div className="project-image">
                    <img
                      className="cover"
                      src={ClimbingNet_2}
                      alt="small prototype"
                    />
                    <p> Small Prototype</p>
                  </div>

                  <div className="detail-header">2. Final Prototype</div>
                  <div className="detail-content">
                    To ensure durability and functionality in extreme winter
                    conditions, we selected a transparent tube with a 10 mm
                    diameter as protective housing for the electronic
                    components. The tube’s flexible material allows it to adapt
                    to the user’s movements without restriction while
                    effectively diffusing the LED light. Initially, capacitive
                    sensors were considered for detecting user interaction.
                    However, usability testing revealed that they were
                    unsuitable for winter environments, as fabric
                    gloves—commonly worn during outdoor play—could not trigger
                    the sensors effectively. To address this limitation, we
                    explored three alternative input mechanisms: flex sensors,
                    pressure sensors, and mechanical buttons. Through
                    performance testing, we found that pressure sensors
                    performed inconsistently within the net structure, while
                    buttons required excessive force to activate. The flex
                    sensor emerged as the most suitable option due to its
                    responsiveness to bending, compatibility with the tubular
                    housing, and durability. Although not highly sensitive to
                    small movements, it provided a reliable and robust
                    interaction method for the intended context. At a later
                    stage, we integrated RGB LED strips (WS2812B) into the
                    tubing, programming each row to display a distinct color
                    pattern. The flex sensors and LED strips were connected to
                    an Arduino Uno, which controlled both lighting and sound
                    responses. The system was programmed so that bending a row
                    triggered corresponding music and light activation. When
                    multiple rows were bent simultaneously, their respective
                    light effects combined, and their sound outputs were
                    harmonically mixed to create a multi-user interactive
                    experience. In the idle state, 16 individual LEDs were
                    programmed to light up in random, dynamic sequences to
                    attract attention. These lights automatically deactivate
                    when a user begins interacting with the climbing nets,
                    ensuring a clear transition between passive and active
                    states.
                  </div>
                  <div className="project-image">
                    <img
                      className="cover"
                      src={ClimbingNet_3}
                      alt="final prototype"
                    />
                    <p> Final Prototype testing in the playground</p>
                  </div>

                  <div className="space"></div>
                  <div className="project-subheader-summary">Evaluation</div>
                  <div className="detail-content">
                    To evaluate the functionality and user experience of the
                    prototype, we conducted user testing in a real outdoor
                    playground environment. Although winter conditions were not
                    yet present at the time of testing, we simulated the
                    low-light environment by performing the test after sunset.
                    Prior to the test, we conducted a site survey at Ekoparken
                    Lekplats to measure the dimensions of existing climbing
                    nets, which served as reference data for scaling our
                    prototype. Based on these measurements, we constructed two
                    human-scale prototypes designed to replicate actual climbing
                    net structures. These prototypes were attached to
                    traditional playground nets to enable realistic interaction
                    testing. During observation, we noted that children
                    initially did not understand the system’s interactive
                    behavior. However, once they began climbing and stepped on
                    the nets, the LED lights activated and music played,
                    prompting immediate curiosity and excitement. On subsequent
                    attempts, children began experimenting intentionally—jumping
                    or stepping on different rows to observe variations in sound
                    and light color. This behavior indicated that the
                    installation successfully encouraged exploratory and playful
                    interaction. Despite the positive engagement, the
                    prototype’s physical structure revealed durability issues.
                    The flexible tubes used to house the electronic components
                    did not consistently return to their original position after
                    bending, resulting in continuous light and sound activation.
                    This issue highlighted a material limitation—the tubing was
                    not adequately resilient for repeated, high-impact play. For
                    future iterations, we plan to replace the tubing with a more
                    elastic and durable material that can maintain flexibility
                    while regaining its original form after deformation. This
                    improvement will enhance both reliability and user safety,
                    ensuring sustained interactivity in real-world playground
                    conditions.
                  </div>
                  <div className="project-image">
                    <img
                      className="cover"
                      src={ClimbingNet_4}
                      alt="evaluation prototype "
                    />
                    <p> Physical Interaction Exhibition</p>
                  </div>
                  <div className="space"></div>
                  <div className="project-subheader-summary">
                    Lesson Learned:
                  </div>
                  <div className="detail-content ml-4">
                    <ol>
                      <li>
                        Early Material Exploration is Critical. <br />
                        Material exploration proved to be a crucial aspect of
                        the design process. Conducting it at an earlier stage
                        would have provided clearer insights into material
                        behavior and limitations, thereby informing more
                        effective design and technical decisions throughout the
                        development process.
                      </li>
                      <li>
                        Idle Mode Enhances User Engagement <br />
                        The idle mode played a significant role in attracting
                        users to the installation. Without it, the climbing nets
                        would have appeared static and uninviting. The visual
                        and auditory cues in idle mode successfully communicated
                        the interactive nature of the design, encouraging
                        spontaneous participation.
                      </li>
                      <li>
                        Sound as a Catalyst for Interaction <br />
                        The integration of music with the climbing nets
                        substantially increased the system’s interactivity and
                        appeal. The auditory feedback drew attention from nearby
                        visitors, many of whom approached the installation after
                        hearing the sounds generated by others. The evolving
                        "soundscape—whether experienced individually or
                        collaboratively" proved to be a powerful motivator for
                        sustained engagement and social play.
                      </li>
                    </ol>
                  </div>
                </div>
                <div className="space"></div>
              </p>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default ProjectDetailClimbingLight;
